import type {
  CreateDungeonCombatInput,
  DungeonActionKind,
  DungeonCombatCommand,
  DungeonCombatExchange,
  DungeonCombatPresentationEvent,
  DungeonCombatResources,
  DungeonDamageResult,
  DungeonCombatState,
  DungeonIntentKind,
  DungeonOpponentResult,
  DungeonPlayerResult,
} from "./dungeon-combat";
import { applyDungeonDamage } from "./dungeon-combat";
import { getDungeonCharacterBonuses } from "./dungeon-character";

export interface DungeonChallengeState {
  version: 1;
  difficulty: "explorer" | "adventurer" | "nightmare";
  profileId: string;
  stage: number;
  route: "disarm" | "bypass" | "pick" | "force" | null;
  puzzleProgress: number;
  slips: number;
  rewardedSteps: number;
}

export type DungeonMonsterStrategy = "damage" | "avoidance" | "opportunity";
export type DungeonMonsterImpact = "claw" | "blade" | "magic" | "projectile" | "crush";

const MONSTER_IMPACTS: Readonly<Record<string, Readonly<Record<DungeonIntentKind, DungeonMonsterImpact>>>> = Object.freeze({
  "cragback-mauler": { attack: "crush", guard: "crush", channel: "crush" },
  "cinder-hounds": { attack: "claw", guard: "claw", channel: "claw" },
  "vault-reaver": { attack: "blade", guard: "blade", channel: "blade" },
  "glass-oracle": { attack: "projectile", guard: "magic", channel: "magic" },
  "mire-arbalist": { attack: "projectile", guard: "projectile", channel: "projectile" },
  "obsidian-shieldbearer": { attack: "crush", guard: "crush", channel: "crush" },
  "gatehouse-fencer": { attack: "blade", guard: "blade", channel: "blade" },
  "hollow-lantern-mage": { attack: "magic", guard: "magic", channel: "magic" },
  "chain-warden": { attack: "blade", guard: "blade", channel: "blade" },
  "three-maw-basilisk": { attack: "claw", guard: "magic", channel: "magic" },
  "raider": { attack: "blade", guard: "blade", channel: "blade" },
  "brute": { attack: "crush", guard: "crush", channel: "crush" },
  "stalker": { attack: "claw", guard: "claw", channel: "claw" },
  "sentinel": { attack: "blade", guard: "blade", channel: "magic" },
  "warden": { attack: "blade", guard: "blade", channel: "magic" },
});

export function getDungeonMonsterImpact(profileId: string, intent: DungeonIntentKind): DungeonMonsterImpact {
  return MONSTER_IMPACTS[profileId]?.[intent] ?? "claw";
}

interface DungeonMonsterTuning {
  assaultBonus: number;
  defenseRiposte: number;
  focusDamage: number;
  opportunityBonus: number;
  responseScale: Record<DungeonActionKind, number>;
  punishedAction: DungeonActionKind;
}

export interface DungeonEncounterProfile {
  id: string;
  kind: "monster" | "boss" | "trap" | "cache";
  name: string;
  subtitle: string;
  artKey: string;
  vitality: number;
  damage: number;
  armor: number;
  tempo: number;
  intents: readonly DungeonIntentKind[];
  intentLabels: Record<DungeonIntentKind, string>;
  secure: DungeonActionKind;
  secureLabel: string;
  releaseLabel: string;
  warning: string;
  strategy: DungeonMonsterStrategy | null;
  recommendations: Record<DungeonIntentKind, DungeonActionKind> | null;
  legacy: boolean;
}

const monster = (
  id: string, kind: "monster" | "boss", name: string, subtitle: string, artKey: string,
  vitality: number, damage: number, armor: number, tempo: number,
  intents: readonly DungeonIntentKind[],
  intentLabels: Record<DungeonIntentKind, string>,
  strategy: DungeonMonsterStrategy,
  recommendations: Record<DungeonIntentKind, DungeonActionKind>,
  legacy = false,
): DungeonEncounterProfile => ({
  id, kind, name, subtitle, artKey,
  vitality, damage, armor, tempo, intents, intentLabels,
  secure: "defense", secureLabel: "", releaseLabel: "", warning: "",
  strategy, recommendations, legacy,
});

const object = (
  id: string, kind: "trap" | "cache", name: string, subtitle: string,
  artKey: string, damage: number, secure: DungeonActionKind,
  secureLabel: string, releaseLabel: string, warning: string,
): DungeonEncounterProfile => ({
  id, kind, name, subtitle, artKey, vitality: 0, damage, armor: 0, tempo: 0,
  intents: ["attack", "guard", "channel"],
  intentLabels: { attack: "TRIGGER ARMED", guard: "PATTERN", channel: "LATCH UNDER LOAD" },
  secure, secureLabel, releaseLabel, warning,
  strategy: null, recommendations: null, legacy: false,
});

const MONSTER_TUNING: Readonly<Record<DungeonMonsterStrategy, DungeonMonsterTuning>> = Object.freeze({
  damage: {
    assaultBonus: 3,
    defenseRiposte: 1,
    focusDamage: 1,
    opportunityBonus: 4,
    responseScale: { assault: 0.72, defense: 0.42, focus: 0.95 },
    punishedAction: "focus",
  },
  avoidance: {
    assaultBonus: 0,
    defenseRiposte: 4,
    focusDamage: 1,
    opportunityBonus: 4,
    responseScale: { assault: 1.15, defense: 0.16, focus: 0.6 },
    punishedAction: "assault",
  },
  opportunity: {
    assaultBonus: -1,
    defenseRiposte: 2,
    focusDamage: 1,
    opportunityBonus: 9,
    responseScale: { assault: 1.18, defense: 0.5, focus: 0.58 },
    punishedAction: "assault",
  },
});

export const DUNGEON_ENCOUNTER_PROFILES: readonly DungeonEncounterProfile[] = [
  monster("cragback-mauler", "monster", "Cragback Mauler", "Survive the crushing wind-up", "brute", 64, 10, 2, 1_200,
    ["attack", "guard", "channel", "attack"],
    { attack: "BOULDER FIST", guard: "STONE BRACE", channel: "EARTHSHAKER" },
    "avoidance", { attack: "defense", guard: "focus", channel: "defense" }),
  monster("cinder-hounds", "monster", "Cinder Hounds", "Break the pack before it surrounds you", "cinderHounds", 54, 7, 0, -800,
    ["attack", "channel", "attack", "guard"],
    { attack: "PACK RUSH", guard: "CIRCLING FEINT", channel: "COORDINATED POUNCE" },
    "damage", { attack: "assault", guard: "defense", channel: "assault" }),
  monster("vault-reaver", "monster", "Vault Reaver", "Bait the shield and punish the opening", "raider", 72, 9, 2, 300,
    ["guard", "attack", "channel", "guard"],
    { attack: "HOOKED CLEAVER", guard: "SPIKED BUCKLER", channel: "BLOOD CHARGE" },
    "opportunity", { attack: "focus", guard: "focus", channel: "assault" }),
  monster("glass-oracle", "monster", "Glass Oracle", "Read the reflection before it answers", "glassOracle", 48, 11, 1, 500,
    ["channel", "guard", "attack", "channel"],
    { attack: "SHARD VOLLEY", guard: "MIRROR WARD", channel: "FUTURE ECHO" },
    "opportunity", { attack: "defense", guard: "focus", channel: "focus" }),
  monster("mire-arbalist", "monster", "Mire Arbalist", "Close the distance before the next bolt", "mireArbalist", 45, 8, 0, -400,
    ["channel", "attack", "guard", "channel"],
    { attack: "SNAP SHOT", guard: "WITHDRAWING AIM", channel: "CRANKING VOLLEY" },
    "damage", { attack: "assault", guard: "focus", channel: "assault" }),
  monster("obsidian-shieldbearer", "monster", "Obsidian Shieldbearer", "Crack the guard, then commit", "obsidianShieldbearer", 68, 9, 4, 700,
    ["guard", "attack", "guard", "channel"],
    { attack: "SHIELD CRUSH", guard: "OBSIDIAN WALL", channel: "FAULTLINE SLAM" },
    "opportunity", { attack: "defense", guard: "focus", channel: "focus" }),
  monster("gatehouse-fencer", "monster", "Gatehouse Fencer", "Draw the parry and answer cleanly", "gatehouseFencer", 52, 7, 1, -600,
    ["guard", "attack", "channel", "guard"],
    { attack: "LUNGING THRUST", guard: "PERFECT PARRY", channel: "KILLING LINE" },
    "opportunity", { attack: "focus", guard: "focus", channel: "defense" }),
  monster("hollow-lantern-mage", "monster", "Hollow Lantern Mage", "Interrupt the flame before it blooms", "hollowLanternMage", 50, 11, 0, 200,
    ["channel", "attack", "channel", "guard"],
    { attack: "EMBER LASH", guard: "SOUL LANTERN", channel: "HOLLOW FLARE" },
    "damage", { attack: "defense", guard: "assault", channel: "assault" }),
  monster("chain-warden", "monster", "Chain Warden", "Weather the hook and counter its recovery", "chainWarden", 60, 10, 3, 900,
    ["attack", "channel", "attack", "guard"],
    { attack: "CHAIN SWEEP", guard: "HOOKED SNARE", channel: "EXECUTION WIND-UP" },
    "avoidance", { attack: "defense", guard: "focus", channel: "defense" }),
  monster("three-maw-basilisk", "boss", "Three-Maw Basilisk", "Endure three attacks, then strike between them", "threeMawBasilisk", 82, 8, 2, 300,
    ["attack", "guard", "attack", "channel", "attack"],
    { attack: "TRIPLE MAUL", guard: "PETRIFYING GAZE", channel: "STONEBREATH" },
    "avoidance", { attack: "defense", guard: "focus", channel: "assault" }),

  // Legacy profiles remain valid for in-progress checkpoints. New encounters
  // only select the commercial roster above.
  monster("raider", "monster", "Gloomfang Raider", "Legacy skirmisher", "raider", 22, 5, 0, -600,
    ["attack", "channel", "attack", "guard"],
    { attack: "QUICK CUT", guard: "BLADE GUARD", channel: "RUNNING LUNGE" },
    "damage", { attack: "defense", guard: "focus", channel: "assault" }, true),
  monster("brute", "monster", "Mossbound Brute", "Legacy juggernaut", "brute", 32, 8, 1, 1_600,
    ["attack", "guard", "channel"],
    { attack: "STONE FIST", guard: "IRON HIDE", channel: "CRUSHING BLOW" },
    "avoidance", { attack: "defense", guard: "focus", channel: "assault" }, true),
  monster("stalker", "monster", "Hollow-Eyed Stalker", "Legacy ambusher", "stalker", 19, 4, 0, -1_000,
    ["guard", "attack", "channel", "attack"],
    { attack: "VENOM JAB", guard: "SHADOW STEP", channel: "POUNCE" },
    "opportunity", { attack: "defense", guard: "focus", channel: "assault" }, true),
  monster("sentinel", "monster", "Cryptbound Sentinel", "Legacy spellguard", "warden", 27, 6, 2, 600,
    ["guard", "channel", "attack", "channel"],
    { attack: "SENTINEL STRIKE", guard: "RUNE ARMOR", channel: "ARCANE BOLT" },
    "opportunity", { attack: "defense", guard: "focus", channel: "assault" }, true),
  monster("warden", "boss", "The Echo Warden", "Legacy guardian", "warden", 42, 6, 1, 500,
    ["attack", "guard", "channel", "attack", "channel"],
    { attack: "HEAVY STRIKE", guard: "WARDEN GUARD", channel: "ECHO SPELL" },
    "avoidance", { attack: "defense", guard: "focus", channel: "assault" }, true),
  object("spikes", "trap", "Runed Spike Floor", "Loaded springs · read the safe plates", "spike", 8,
    "defense", "Pin the Spring", "Cut the Trigger Link", "Loaded spring: pin it before cutting."),
  object("darts", "trap", "Ember Dart Gallery", "Dart volley · block the nozzle", "dart", 7,
    "focus", "Plug the Nozzle", "Unhook the Tripwire", "Block the nozzle before touching the wire."),
  object("runes", "trap", "Resonant Rune Snare", "Arcane circuit · ground its charge", "rune", 9,
    "focus", "Ground the Rune", "Lift the Anchor Seal", "The circuit is live: ground it first."),
  object("needle-coffer", "cache", "Delver’s Needlebox", "Needle trigger · two-pin lock", "strongbox", 7,
    "defense", "Clamp the Needle", "Open the Lid", "Loaded needle: clamp it before opening."),
  object("rune-coffer", "cache", "Echo Reliquary", "Hex trigger · engraved rune lock", "coffer", 8,
    "focus", "Drain the Hex", "Release the Seal", "Charged seal: drain it before opening."),
  object("iron-coffer", "cache", "Ironbound Strongbox", "Pressure trigger · reinforced three-pin lock", "strongbox", 9,
    "defense", "Brace the Pressure Plate", "Lift the Lid", "Pressure plate: brace it before opening."),
];

const GLYPHS = [
  { id: "sun", label: "Sun", symbol: "☀" },
  { id: "moon", label: "Moon", symbol: "☾" },
  { id: "star", label: "Star", symbol: "✦" },
] as const;
const PINS = [
  { id: "diamond", label: "Diamond", symbol: "◇" },
  { id: "circle", label: "Circle", symbol: "○" },
  { id: "triangle", label: "Triangle", symbol: "△" },
] as const;
const ACTIONS: readonly DungeonActionKind[] = ["assault", "defense", "focus"];

export interface DungeonChallengeAction {
  kind: DungeonActionKind;
  label: string;
  description: string;
  cue: string;
  icon?: string;
  recommended?: boolean;
}

export interface DungeonChallengeView {
  profileId: string;
  name: string;
  subtitle: string;
  objective: string;
  meterLabel: string;
  stageId: string;
  stageLabel: string;
  stageIndex: number;
  stageCount: number;
  timerLabel: string;
  intentLabel: string;
  incomingDamage: number;
  armor: number;
  currentVitality: number;
  maximumVitality: number;
  artKey: string;
  strategy: DungeonMonsterStrategy | null;
  recommendedAction: DungeonActionKind | null;
  puzzle: null | { label: string; sequence: { id: string; label: string; symbol: string }[]; progress: number };
  actions: DungeonChallengeAction[];
}

// Avalanche the full seed before selection. FNV low-bit/modulo selection made
// alternating cursors repeat the same two-intent pattern in the old rules.
function mix(value: number): number {
  value = Math.imul(value ^ (value >>> 16), 0x7feb352d);
  value = Math.imul(value ^ (value >>> 15), 0x846ca68b);
  return (value ^ (value >>> 16)) >>> 0;
}

function identitySeed(runSeed: number, encounterId: string): number {
  let value = Math.floor(runSeed) >>> 0;
  for (const char of encounterId) value = mix(value ^ char.charCodeAt(0));
  return value;
}

export function getDungeonEncounterProfile(runSeed: number, encounterId: string, kind: string): DungeonEncounterProfile | null {
  const candidates = DUNGEON_ENCOUNTER_PROFILES.filter((profile) => profile.kind === kind && !profile.legacy);
  return candidates[identitySeed(runSeed, encounterId) % candidates.length] ?? null;
}

function profileOf(combat: DungeonCombatState): DungeonEncounterProfile | null {
  return DUNGEON_ENCOUNTER_PROFILES.find((profile) => profile.id === combat.challenge?.profileId) ?? null;
}

function puzzleOf(combat: DungeonCombatState): { id: string; label: string; symbol: string }[] {
  const isPins = combat.encounterKind === "cache" && combat.challenge?.profileId !== "rune-coffer";
  const palette = isPins ? PINS : GLYPHS;
  const length = combat.challenge?.profileId === "iron-coffer" || combat.danger >= 3 ? 3 : 2;
  const offset = mix(combat.patternSeed ^ 0x13579) % 3;
  const reverse = (mix(combat.patternSeed ^ 0x24680) & 1) === 1;
  return Array.from({ length }, (_, index) => ({ ...palette[(offset + index * (reverse ? 2 : 1)) % 3]! }));
}

function nextMonsterIntent(combat: DungeonCombatState, profile: DungeonEncounterProfile): DungeonIntentKind {
  // A rotated archetype-specific bag guarantees a wind-up and guard opportunity
  // every cycle; it never changes in response to the action just chosen.
  const offset = mix(combat.patternSeed) % profile.intents.length;
  return profile.intents[(offset + combat.patternCursor) % profile.intents.length] ?? "attack";
}

function responseDamage(combat: DungeonCombatState, profile: DungeonEncounterProfile): number {
  const difficultyScale = combat.challenge?.difficulty === "explorer" ? 0.8 : combat.challenge?.difficulty === "nightmare" ? 1.25 : 1;
  const base = Math.round((profile.damage + Math.max(0, combat.danger - 1)) * difficultyScale);
  const enraged = (profile.id === "warden" || profile.id === "three-maw-basilisk") && combat.integrity <= combat.maximumIntegrity * 0.5 ? 2 : 0;
  return Math.max(2, base + enraged + (combat.intent === "channel" ? 3 : combat.intent === "guard" ? -1 : 0));
}

function isArcaneAttack(combat: DungeonCombatState, profile: DungeonEncounterProfile): boolean {
  return profile.id === "runes" || profile.id === "rune-coffer" ||
    ((profile.id === "sentinel" || profile.id === "warden" || profile.id === "glass-oracle" || profile.id === "hollow-lantern-mage") && combat.intent === "channel");
}

function action(kind: DungeonActionKind, label: string, cue: string, description = cue, icon?: string, recommended = false): DungeonChallengeAction {
  return { kind, label, cue, description, ...(icon ? { icon } : {}), ...(recommended ? { recommended: true } : {}) };
}

function monsterTuning(profile: DungeonEncounterProfile): DungeonMonsterTuning {
  return MONSTER_TUNING[profile.strategy ?? "damage"];
}

function recommendedMonsterAction(combat: DungeonCombatState, profile: DungeonEncounterProfile): DungeonActionKind {
  if (combat.opening) return "assault";
  return profile.recommendations?.[combat.intent] ?? "assault";
}

function syncTaskState(combat: DungeonCombatState, character?: DungeonCombatResources["character"]): void {
  const challenge = combat.challenge;
  const profile = profileOf(combat);
  if (!challenge || !profile) return;
  if (profile.kind === "monster" || profile.kind === "boss") {
    combat.intent = nextMonsterIntent(combat, profile);
    combat.reactionWindowMs = Math.max(3_500, 8_500 + profile.tempo - (combat.danger - 1) * 400 - (challenge.difficulty === "nightmare" ? 2_500 : challenge.difficulty === "explorer" ? -3_500 : 0));
  } else {
    const timed = profile.kind === "trap" ? challenge.stage === 2 : challenge.stage === 1;
    combat.reactionWindowMs = timed
      ? Math.max(3_500, 8_500 - (combat.danger - 1) * 500 - (challenge.difficulty === "nightmare" ? 2_500 : challenge.difficulty === "explorer" ? -3_500 : 0)) + getDungeonCharacterBonuses(character?.stats).mechanismWindowMs
      : null;
    combat.intent = timed ? "attack" : challenge.stage === 1 || challenge.stage === 3 ? "guard" : "channel";
    const puzzleLength = puzzleOf(combat).length;
    if (profile.kind === "trap") {
      combat.integrity = challenge.stage >= 4 ? 0 : challenge.stage === 0 ? puzzleLength + 3
        : challenge.route === "bypass" ? 1
        : challenge.stage === 1 ? puzzleLength - challenge.puzzleProgress + 2
        : challenge.stage === 2 ? 2 : challenge.stage === 3 ? 1 : 0;
    } else {
      const lockLength = challenge.route === "force" ? 2 : puzzleLength;
      combat.integrity = challenge.stage === 0 ? puzzleLength + 4
        : challenge.stage === 1 ? puzzleLength + 3
        : challenge.stage === 2 ? puzzleLength + 2
        : challenge.stage === 3 ? lockLength - challenge.puzzleProgress + 1
        : challenge.stage === 4 ? 1 : 0;
    }
  }
  combat.timeRemainingMs = combat.reactionWindowMs ?? 0;
}

export function initializeDungeonChallenge(combat: DungeonCombatState, input: CreateDungeonCombatInput): void {
  if (input.legacyRules || input.encounterKind === "entrance") return;
  const profile = getDungeonEncounterProfile(input.runSeed, input.encounterId, input.encounterKind);
  if (!profile) return;
  const baseTimeout = input.damageOnTimeout - Math.max(0, input.danger - 1) * 2;
  const difficulty = input.difficulty ?? (baseTimeout <= 8 ? "explorer" : baseTimeout >= 18 ? "nightmare" : "adventurer");
  combat.challenge = { version: 1, difficulty, profileId: profile.id, stage: 0, route: null, puzzleProgress: 0, slips: 0, rewardedSteps: 0 };
  if (profile.kind === "monster" || profile.kind === "boss") {
    // Commercial-roster vitality is authored as an exact matchup contract.
    // Danger still changes tempo and incoming damage without silently turning
    // the same creature into a different health model.
    combat.maximumIntegrity = profile.vitality + (profile.legacy
      ? Math.max(0, combat.danger - 1) * (profile.kind === "boss" ? 4 : 2)
      : 0);
    combat.integrity = combat.maximumIntegrity;
  }
  syncTaskState(combat, input.character);
  if (profile.kind === "trap" || profile.kind === "cache") combat.maximumIntegrity = combat.integrity;
}

export function getDungeonChallengeView(combat: DungeonCombatState, resources?: DungeonCombatResources): DungeonChallengeView | null {
  const profile = profileOf(combat);
  const challenge = combat.challenge;
  if (!profile || !challenge) return null;
  const isMonster = profile.kind === "monster" || profile.kind === "boss";
  const view: DungeonChallengeView = {
    profileId: profile.id, name: profile.name, subtitle: profile.subtitle, artKey: profile.artKey,
    objective: "Defeat the threat", meterLabel: isMonster ? "VITALITY" : "TASKS",
    stageId: "combat", stageLabel: profile.intentLabels[combat.intent], stageIndex: challenge.stage,
    stageCount: isMonster ? 0 : profile.kind === "trap" ? challenge.route === "bypass" ? 2 : 4 : 5,
    timerLabel: isMonster ? "YOUR MOVE" : "TAKE YOUR TIME", intentLabel: profile.intentLabels[combat.intent],
    incomingDamage: Math.max(1, (isMonster ? responseDamage(combat, profile) : profile.damage + combat.danger - 1) -
      (isArcaneAttack(combat, profile) ? getDungeonCharacterBonuses(resources?.character?.stats).arcaneProtection : 0)),
    armor: profile.armor, currentVitality: combat.integrity, maximumVitality: combat.maximumIntegrity,
    strategy: profile.strategy, recommendedAction: null,
    puzzle: null, actions: [],
  };
  if (isMonster) {
    const tuning = monsterTuning(profile);
    const recommended = recommendedMonsterAction(combat, profile);
    const guarded = combat.intent === "guard";
    const armor = combat.opening ? 0 : profile.armor + (guarded ? profile.id === "stalker" ? 3 : 2 : 0);
    const normalDamage = Math.max(1, 4 + Math.min(2, resources?.weaponBonus ?? 0) + tuning.assaultBonus +
      (combat.opening ? tuning.opportunityBonus : 0) - armor) +
      getDungeonCharacterBonuses(resources?.character?.stats).strengthDamage;
    const responseScale = tuning.responseScale[recommended] * 0.72;
    const blockPercent = Math.max(0, Math.min(90, Math.round((1 - responseScale) * 100)));
    view.objective = `${profile.intentLabels[combat.intent]} · ${profile.strategy?.toUpperCase()} MATCHUP`;
    view.recommendedAction = recommended;
    if ((profile.id === "warden" || profile.id === "three-maw-basilisk") && combat.integrity <= combat.maximumIntegrity * 0.5) {
      view.subtitle = "ENRAGED · stronger responses below half vitality";
    }
    view.actions = [
      action("assault", combat.opening ? "Exploit Opening" : "Attack", `${normalDamage} DAMAGE${combat.opening ? " · ARMOR PIERCED" : recommended === "assault" ? " · PRESS NOW" : " · HIGH RISK"}`, "Commit to direct damage. Fast clean typing adds 2–4 damage; the surviving opponent always responds.", undefined, recommended === "assault"),
      action("defense", "Guard & Riposte", `${tuning.defenseRiposte} DAMAGE · BLOCK ${recommended === "defense" ? blockPercent : Math.round((1 - tuning.responseScale.defense) * 100)}%`, "Absorb the exchange and answer with a controlled riposte. Swift timing improves both the riposte and mitigation.", undefined, recommended === "defense"),
      action("focus", guarded ? "Break Guard" : "Create Opening", `${tuning.focusDamage} DAMAGE · NEXT +${tuning.opportunityBonus}`, "Read the opponent, deal bypass damage, and prepare an armor-piercing follow-up attack.", undefined, recommended === "focus"),
    ];
    return view;
  }
  const stage = challenge.stage;
  if (profile.kind === "trap") {
    view.objective = "Read the hazard · disarm or bypass";
    if (stage === 0) {
      view.stageId = "inspect"; view.stageLabel = "Choose your approach";
      view.actions = [
        action("focus", "Inspect the Trigger", "DISARM ROUTE · SAFER", "Read the plates, solve their pattern, then secure the mechanism."),
        action("defense", "Find a Bypass", "SHORT ROUTE · TIMED", "Cross without disarming; accurate fast typing avoids the hazard."),
      ];
    } else if (stage === 1) {
      view.stageId = "plate-puzzle"; view.stageLabel = "Read the safe plate sequence";
      view.puzzle = { label: "Follow the plates, left to right", sequence: puzzleOf(combat), progress: challenge.puzzleProgress };
      view.actions = GLYPHS.map((glyph, index) => action(ACTIONS[index]!, `Step on ${glyph.label}`, "MATCH THE NEXT PLATE", "A wrong plate fires the mechanism.", glyph.id));
    } else if (stage === 2 && challenge.route === "bypass") {
      view.stageId = "bypass"; view.stageLabel = "Cross between activations"; view.timerLabel = "CROSS NOW";
      view.actions = [
        action("assault", profile.id === "darts" ? "Sprint Between Volleys" : "Leap the Hazard", "SWIFT: SAFE · SLOW: HIT", "Fast accurate typing clears the hazard; a slow crossing costs vitality."),
        action("defense", "Cross Behind Your Shield", "GUARANTEED · SMALL HIT", "Accept reduced damage for a reliable crossing."),
      ];
    } else if (stage === 2) {
      view.stageId = "secure"; view.stageLabel = profile.warning; view.timerLabel = "SECURE NOW";
      view.actions = [
        action(profile.secure, profile.secureLabel, "NEUTRALIZE THE HAZARD", profile.warning),
        action(profile.secure === "assault" ? "defense" : "assault", "Cut the Live Trigger", "FIRES THE TRAP", "Cutting under load releases the stored force."),
      ];
    } else {
      view.stageId = "release"; view.stageLabel = "Mechanism secured · finish disarming";
      view.actions = [action("assault", profile.releaseLabel, "MAKE THE PASSAGE SAFE")];
    }
  } else {
    view.objective = "Inspect → disarm → unlock";
    if (stage === 0) {
      view.stageId = "inspect"; view.stageLabel = "A trigger is hidden in the latch";
      view.actions = [
        action("focus", "Inspect the Latch", "REVEAL THE TRIGGER"),
        action("assault", "Force the Trapped Lid", "TRAP HIT · SKIP TO LOCK", "Accept a full trap hit to break the trigger and reach the lock."),
      ];
    } else if (stage === 1) {
      view.stageId = "disarm-trigger"; view.stageLabel = profile.warning; view.timerLabel = "DISARM NOW";
      view.actions = [
        action(profile.secure, profile.secureLabel, "DISARM THE TRIGGER", profile.warning),
        action("assault", "Tear Out the Trigger", "TRAP HIT · REACH LOCK", "The trigger fires, but the lock becomes safe to work on."),
      ];
    } else if (stage === 2) {
      view.stageId = "choose-lock"; view.stageLabel = "Trigger safe · choose how to open the lock";
      view.actions = [
        action("focus", profile.id === "rune-coffer" ? "Decode the Rune Lock" : "Pick the Lock", "PATTERN · BONUS SCORE", "Match the pin or rune sequence to earn careful-entry score."),
        action("assault", "Break the Lock", "TWO STRIKES · NO BONUS", "Drive a wedge and pry the lock apart; no puzzle bonus."),
      ];
    } else if (stage === 3 && challenge.route === "force") {
      view.stageId = "force-lock"; view.stageLabel = `${challenge.puzzleProgress}/2 lock strikes`;
      view.actions = [action("assault", challenge.puzzleProgress === 0 ? "Drive the Wedge" : "Pry the Lock Apart", "BREAK THE LOCK")];
    } else if (stage === 3) {
      view.stageId = "lock-puzzle"; view.stageLabel = profile.id === "rune-coffer" ? "Trace the engraved rune sequence" : "Set the pins in engraved order";
      view.puzzle = { label: profile.id === "rune-coffer" ? "Match the runes, left to right" : "Set the pins, left to right", sequence: puzzleOf(combat), progress: challenge.puzzleProgress };
      const glyphs = profile.id === "rune-coffer" ? GLYPHS : PINS;
      view.actions = glyphs.map((glyph, index) => action(ACTIONS[index]!, `${profile.id === "rune-coffer" ? "Trace" : "Set"} ${glyph.label}`, "MATCH THE NEXT SYMBOL", "A wrong symbol resets this lock sequence, not the disarmed trigger.", glyph.id));
    } else {
      view.stageId = "open"; view.stageLabel = "Trigger disarmed · lock released";
      view.actions = [action("defense", profile.releaseLabel, "CLAIM THE RELIC")];
    }
  }
  view.intentLabel = view.stageLabel;
  if (profile.kind === "trap" && challenge.route === "bypass") view.stageIndex = stage === 0 ? 0 : 1;
  return view;
}

export function isValidDungeonChallenge(value: unknown, kind: string, danger = 1): value is DungeonChallengeState {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;
  const profile = DUNGEON_ENCOUNTER_PROFILES.find((entry) => entry.id === candidate.profileId && entry.kind === kind);
  const puzzleLength = profile?.id === "iron-coffer" || danger >= 3 ? 3 : 2;
  const stage = Number(candidate.stage), progress = Number(candidate.puzzleProgress);
  const coherent = kind === "monster" || kind === "boss"
    ? stage === 0 && candidate.route === null && progress === 0
    : kind === "trap"
      ? stage === 0 ? candidate.route === null && progress === 0
        : stage === 1 ? candidate.route === "disarm" && progress < puzzleLength
          : candidate.route === "bypass" ? (stage === 2 || stage === 4) && progress === 0
            : candidate.route === "disarm" && progress === puzzleLength
      : stage <= 2 ? candidate.route === null && progress === 0
        : candidate.route === "pick" ? stage === 3 ? progress < puzzleLength : progress === puzzleLength
          : candidate.route === "force" && (stage === 3 ? progress < 2 : progress === 2);
  return Boolean(profile) && coherent && candidate.version === 1 && ["explorer", "adventurer", "nightmare"].includes(String(candidate.difficulty)) && Number.isInteger(candidate.stage) && Number(candidate.stage) >= 0 && Number(candidate.stage) <= (kind === "trap" ? 4 : 5)
    && [null, "disarm", "bypass", "pick", "force"].includes(candidate.route as null | string)
    && Number.isInteger(candidate.puzzleProgress) && Number(candidate.puzzleProgress) >= 0 && Number(candidate.puzzleProgress) <= 3
    && Number.isInteger(candidate.slips) && Number(candidate.slips) >= 0
    && Number.isInteger(candidate.rewardedSteps) && Number(candidate.rewardedSteps) >= 0 && Number(candidate.rewardedSteps) <= 0xffffff;
}

export function getDungeonSwiftBonus(command: DungeonCombatCommand, difficulty = "adventurer", graceMs = 0): number {
  if (command.type !== "submit-action" || !command.firstAttemptCorrect || !command.timing) return 0;
  const { elapsedMs, wordLength } = command.timing;
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0 || !Number.isInteger(wordLength) || wordLength < 1 || wordLength > 64) return 0;
  const swiftWindow = 700 + wordLength * (difficulty === "explorer" ? 280 : difficulty === "nightmare" ? 180 : 220) + Math.max(0, Math.min(1_000, Number.isFinite(graceMs) ? graceMs : 0));
  return elapsedMs <= swiftWindow * 0.55 ? 2 : elapsedMs <= swiftWindow ? 1 : 0;
}

function result(actor: DungeonOpponentResult["actor"], intent: DungeonIntentKind, vitalityDamage: number, fullStrength = false): DungeonOpponentResult {
  return { actor, intent, outcome: vitalityDamage > 0 ? "attack" : "absorbed", countered: false, fullStrength, vitalityDamage, integrityRestore: 0, powerChange: 0, wardSpent: 0 };
}

function addResponse(events: DungeonCombatPresentationEvent[], response: DungeonOpponentResult, actionLabel: string, resultLabel: string): void {
  const vitalityLoss = Math.max(0, response.vitalityDamage - (response.temporaryDamage || 0));
  const visibleResult = response.temporaryDamage
    ? `AEGIS −${response.temporaryDamage}${vitalityLoss ? ` · VITALITY −${vitalityLoss}` : ""}`
    : resultLabel;
  events.push(
    { beat: "opponent-action", actor: response.actor, intent: response.intent, label: actionLabel },
    { beat: "opponent-result", actor: response.actor, intent: response.intent, outcome: response.outcome,
      amount: response.vitalityDamage || undefined, temporaryAmount: response.temporaryDamage, label: visibleResult },
  );
}

export function resolveDungeonChallengeExchange(input: {
  combat: DungeonCombatState; resources: DungeonCombatResources; command: DungeonCombatCommand; id: string;
}): DungeonCombatExchange {
  const combat: DungeonCombatState = { ...input.combat, challenge: { ...input.combat.challenge! } };
  const resources = { ...input.resources };
  const profile = profileOf(combat)!;
  const challenge = combat.challenge!;
  const command = input.command;
  const bonuses = getDungeonCharacterBonuses(resources.character?.stats);
  const events: DungeonCombatPresentationEvent[] = [];
  let playerResult: DungeonPlayerResult | null = null;
  let opponentResult: DungeonOpponentResult | null = null;
  const isMonster = profile.kind === "monster" || profile.kind === "boss";
  const actor = isMonster ? "enemy" : profile.kind === "trap" ? "mechanism" : "seal";
  const packet = (accepted = true): DungeonCombatExchange => ({ id: input.id, accepted, command: command.type, nextCombat: combat, nextResources: resources, playerResult, opponentResult, events });
  if (combat.status !== "player-ready") return packet(false);
  let lastDamage: DungeonDamageResult = { totalDamage: 0, temporaryDamage: 0, vitalityDamage: 0 };
  const damage = (amount: number) => { lastDamage = applyDungeonDamage(resources, amount); return lastDamage; };
  const arcane = isArcaneAttack(combat, profile);
  const protect = (amount: number) => Math.max(1, amount - (arcane ? bonuses.arcaneProtection : Math.max(0, resources.armorBonus || 0)));
  const hazardDamage = protect(profile.damage + Math.max(0, combat.danger - 1));
  if (command.type === "use-potion") {
    if ((resources.potions || 0) < 1 || resources.vitality >= resources.maximumVitality) return packet(false);
    const restored = Math.min(30, resources.maximumVitality - resources.vitality);
    resources.potions = (resources.potions || 0) - 1;
    resources.vitality += restored;
    combat.commandSequence += 1;
    events.push(
      { beat: "player-action", actor: "player", action: "potion", label: "DRINK POTION" },
      { beat: "player-result", actor: "player", action: "potion", amount: restored, label: `+${restored} VITALITY` },
    );
    if (isMonster || combat.reactionWindowMs !== null) {
      let incoming = isMonster ? Math.max(1, Math.ceil(responseDamage(combat, profile))) : hazardDamage;
      const wardSpent = resources.ward > 0 ? 1 : 0;
      if (wardSpent) { incoming = Math.max(1, incoming - 3); resources.ward -= 1; }
      if (isMonster) incoming = protect(incoming);
      opponentResult = result(actor, combat.intent, incoming, true);
      opponentResult.wardSpent = wardSpent;
      if (isMonster) opponentResult.outcome = combat.intent === "guard" ? "riposte" : combat.intent === "channel" ? "spellstrike" : "attack";
      opponentResult.temporaryDamage = damage(incoming).temporaryDamage;
      addResponse(events, opponentResult, isMonster ? profile.intentLabels[combat.intent] : "MECHANISM FIRES", `−${incoming} VITALITY`);
    }
    if (isMonster) { combat.previousIntent = combat.intent; combat.patternCursor += 1; }
    combat.round += 1;
    syncTaskState(combat, resources.character);
    if (resources.vitality <= 0) { combat.status = "defeat"; events.push({ beat: "defeat", actor: "player", label: "Expedition defeated" }); }
    else events.push({ beat: "next-intent", actor, intent: combat.intent, label: getDungeonChallengeView(combat, resources)!.stageLabel });
    return packet();
  }
  if (command.type === "mistype") {
    // Once a chest's trigger is neutralized, a typing error slips a pin rather
    // than magically resurrecting its trap. That safety is earned and saved.
    const safeLock = profile.kind === "cache" && challenge.stage >= 2;
    const amount = safeLock ? 0 : protect(combat.damageOnMistake);
    const applied = damage(amount); challenge.slips += 1;
    const picking = safeLock && challenge.stage === 3 && challenge.route === "pick";
    if (picking) { challenge.puzzleProgress = 0; syncTaskState(combat, resources.character); }
    combat.commandSequence += 1;
    events.push({ beat: "opportunity", actor, intent: combat.intent, amount,
      temporaryAmount: applied.temporaryDamage,
      label: safeLock ? `${picking ? "PICK" : "TOOL"} SLIPPED · TRY AGAIN`
        : applied.temporaryDamage ? `AEGIS −${applied.temporaryDamage}${applied.vitalityDamage ? ` · VITALITY −${applied.vitalityDamage}` : ""}`
          : `${isMonster ? "OPPORTUNITY HIT" : "TRIGGER FIRES"} · −${amount}` });
    if (resources.vitality <= 0) { combat.status = "defeat"; events.push({ beat: "defeat", actor: "player", label: "Expedition defeated" }); }
    return packet();
  }
  if (command.type === "threat-expired") {
    if (combat.reactionWindowMs === null) return packet(false);
    const amount = isMonster ? protect(Math.max(combat.damageOnTimeout, responseDamage(combat, profile))) : hazardDamage;
    opponentResult = result(actor, combat.intent, amount, true);
    opponentResult.temporaryDamage = damage(amount).temporaryDamage; combat.commandSequence += 1;
    addResponse(events, opponentResult, isMonster ? profile.intentLabels[combat.intent] : "MECHANISM FIRES", `−${amount} VITALITY`);
    if (isMonster) { combat.previousIntent = combat.intent; combat.patternCursor += 1; combat.round += 1; }
    syncTaskState(combat, resources.character);
  } else {
    const view = getDungeonChallengeView(combat, resources)!;
    const selected = view.actions.find((offer) => offer.kind === command.action);
    if (!selected) return packet(false);
    const taskBit = 1 << (challenge.stage * 4 + challenge.puzzleProgress);
    const speedTier = getDungeonSwiftBonus(command, challenge.difficulty, bonuses.swiftWindowMs);
    const swift = speedTier > 0;
    const speedLabel = speedTier === 2 ? "BLAZING · " : swift ? "SWIFT · " : "";
    let progress = 0, success = true, label = "ACTION COMPLETE", multiplier = 1;
    events.push({ beat: "player-action", actor: "player", action: command.action, label: selected.label });
    combat.commandSequence += 1;
    if (isMonster) {
      const opening = combat.opening;
      const tuning = monsterTuning(profile);
      const recommended = recommendedMonsterAction(input.combat, profile);
      if (command.action === "assault") {
        const armor = opening ? 0 : profile.armor + (combat.intent === "guard" ? profile.id === "stalker" ? 3 : 2 : 0);
        progress = Math.max(1, 4 + Math.min(2, resources.weaponBonus) + tuning.assaultBonus +
          speedTier * 2 + (opening ? tuning.opportunityBonus : 0) - armor) + bonuses.strengthDamage;
        combat.opening = false;
        label = `${speedLabel}${progress} DAMAGE${opening ? " · OPENING EXPLOITED" : ""}`;
      } else if (command.action === "focus") {
        progress = tuning.focusDamage + speedTier * 2;
        combat.opening = true;
        label = `${speedLabel}${progress} DAMAGE · OPENING +${tuning.opportunityBonus}`;
      } else {
        progress = tuning.defenseRiposte + speedTier * 2;
        label = `${speedLabel}RIPOSTE ${progress} · GUARD RAISED`;
      }
      combat.integrity = Math.max(0, combat.integrity - progress);
      if (combat.integrity > 0) {
        let responseScale = tuning.responseScale[command.action];
        if (command.action === recommended) responseScale *= 0.72;
        else if (command.action === tuning.punishedAction) responseScale *= 1.12;
        if (command.action === "defense" && swift) responseScale *= 0.75;
        if (command.action === "assault" && opening) responseScale *= 0.55;
        // A clean assault can blunt a telegraphed wind-up, but never deletes
        // the enemy's turn. The response still resolves exactly once.
        if (command.action === "assault" && combat.intent === "channel" && swift) responseScale *= 0.72;
        if (speedTier === 2) responseScale *= 0.78;
        else if (speedTier === 1) responseScale *= 0.9;
        let incoming = Math.max(1, Math.ceil(responseDamage(combat, profile) * responseScale));
        const wardSpent = resources.ward > 0 ? 1 : 0;
        if (wardSpent) { incoming = Math.max(1, incoming - 3); resources.ward -= 1; }
        incoming = protect(incoming);
        opponentResult = result("enemy", combat.intent, incoming);
        opponentResult.wardSpent = wardSpent;
        opponentResult.outcome = combat.intent === "guard" ? "riposte" : combat.intent === "channel" ? "spellstrike" : "attack";
        opponentResult.temporaryDamage = damage(incoming).temporaryDamage;
      }
      multiplier = (speedTier === 2 ? 1.65 : swift ? 1.35 : 1) * (command.action === recommended ? 1.15 : 1);
    } else if (profile.kind === "trap") {
      if (challenge.stage === 0) {
        challenge.route = command.action === "defense" ? "bypass" : "disarm";
        challenge.stage = challenge.route === "bypass" ? 2 : 1;
        label = challenge.route === "bypass" ? "BYPASS FOUND · CROSS NOW" : "TRIGGER REVEALED";
      } else if (challenge.stage === 1) {
        const expected = puzzleOf(combat)[challenge.puzzleProgress]!;
        success = selected.icon === expected.id;
        if (success) {
          challenge.puzzleProgress += 1; label = "SAFE PLATE";
          if (challenge.puzzleProgress >= puzzleOf(combat).length) { challenge.stage = 2; label = "PATTERN SOLVED · SECURE NOW"; }
        } else { challenge.puzzleProgress = 0; damage(hazardDamage); label = "WRONG PLATE · TRAP FIRES"; }
      } else if (challenge.route === "bypass") {
        const amount = command.action === "defense" ? Math.max(1, Math.ceil(hazardDamage * 0.3)) : swift ? 0 : Math.ceil(hazardDamage * 0.65);
        damage(amount); challenge.stage = 4; label = amount ? `PASSAGE CROSSED · −${amount}` : "SWIFT CROSSING · SAFE";
      } else if (challenge.stage === 2) {
        success = command.action === profile.secure;
        if (success) { challenge.stage = 3; label = "MECHANISM SECURED"; }
        else { damage(hazardDamage); label = "LIVE TRIGGER · TRAP FIRES"; }
      } else { challenge.stage = 4; label = "TRAP DISARMED"; }
      syncTaskState(combat, resources.character);
      progress = Math.max(0, input.combat.integrity - combat.integrity);
      if (!success || (challenge.route === "bypass" && lastDamage.totalDamage > 0)) {
        opponentResult = result(actor, input.combat.intent, lastDamage.totalDamage);
        opponentResult.temporaryDamage = lastDamage.temporaryDamage;
      }
    } else {
      if (challenge.stage === 0) {
        if (command.action === "assault") { damage(hazardDamage); challenge.stage = 2; label = "TRIGGER BROKEN · TRAP HIT"; }
        else { challenge.stage = 1; label = "HIDDEN TRIGGER FOUND"; }
      } else if (challenge.stage === 1) {
        if (command.action !== profile.secure) { damage(hazardDamage); label = "TRIGGER FIRES · LOCK EXPOSED"; }
        else label = "TRIGGER DISARMED";
        challenge.stage = 2;
      } else if (challenge.stage === 2) {
        challenge.route = command.action === "focus" ? "pick" : "force";
        challenge.stage = 3; challenge.puzzleProgress = 0;
        label = challenge.route === "pick" ? "MATCH THE LOCK PATTERN" : "WEDGE THE LOCK";
      } else if (challenge.stage === 3) {
        success = challenge.route === "force" || selected.icon === puzzleOf(combat)[challenge.puzzleProgress]!.id;
        if (success) {
          challenge.puzzleProgress += 1;
          const length = challenge.route === "force" ? 2 : puzzleOf(combat).length;
          label = challenge.route === "force" ? "LOCK WEAKENED" : "PIN SET";
          if (challenge.puzzleProgress >= length) { challenge.stage = 4; label = "LOCK RELEASED"; if (challenge.route === "pick") multiplier = 1.75; }
        } else { challenge.puzzleProgress = 0; challenge.slips += 1; label = "PIN SLIPPED · PATTERN RESET"; }
      } else { challenge.stage = 5; label = "COFFER OPENED"; }
      syncTaskState(combat, resources.character);
      progress = Math.max(0, input.combat.integrity - combat.integrity);
      if (lastDamage.totalDamage > 0) {
        opponentResult = result(actor, input.combat.intent, lastDamage.totalDamage);
        opponentResult.temporaryDamage = lastDamage.temporaryDamage;
      }
    }
    combat.lastPlayerAction = command.action;
    if (!isMonster && success) {
      if ((challenge.rewardedSteps & taskBit) !== 0) multiplier = 0;
      challenge.rewardedSteps |= taskBit;
    }
    playerResult = { action: command.action, outcome: success ? "counter" : "resisted", integrityDamage: progress, wardGain: 0,
      openingGranted: isMonster && command.action === "focus", openingConsumed: isMonster && command.action === "assault" && input.combat.opening,
      scoreMultiplier: success ? multiplier : 0, swift, label };
    events.push({ beat: "player-result", actor: "player", action: command.action, outcome: playerResult.outcome, amount: progress || undefined, label });
    if (opponentResult) addResponse(events, opponentResult, isMonster ? profile.intentLabels[input.combat.intent] : "TRIGGER FIRES", `−${opponentResult.vitalityDamage} VITALITY`);
    combat.round += 1;
    if (isMonster) { combat.previousIntent = combat.intent; combat.patternCursor += 1; syncTaskState(combat, resources.character); }
  }
  if (resources.vitality <= 0) { combat.status = "defeat"; events.push({ beat: "defeat", actor: "player", label: "Expedition defeated" }); }
  else if (combat.integrity <= 0) { combat.status = "victory"; events.push({ beat: "victory", actor: "player", label: isMonster ? "Enemy defeated" : profile.kind === "trap" ? "Passage safe" : "Coffer opened" }); }
  else events.push({ beat: "next-intent", actor, intent: combat.intent, label: getDungeonChallengeView(combat, resources)!.stageLabel });
  return packet();
}
